import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import LazyImage from '../../components/base/LazyImage';
import { projects as allProjects } from '../../mocks/projects';

const categories = ['All', 'Web Apps', 'E-commerce', 'Landing Pages'];

// Floating particle component
const Particle = ({ delay, duration, size, left, top }: { delay: number; duration: number; size: number; left: string; top: string }) => (
  <div 
    className="absolute rounded-full bg-[#52B788]/20 animate-float pointer-events-none"
    style={{
      width: size,
      height: size,
      left,
      top,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
    }}
  />
);

// Hook for intersection observer
const useInView = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
};

// Project card component with animations
const ProjectCard = ({ project, index }: { project: typeof allProjects[0]; index: number }) => {
  const { ref, isInView } = useInView(0.2);
  const isEven = index % 2 === 0;

  const descriptionText = [
    `Problem: ${project.fullDescription.problem}`,
    `Solution: ${project.fullDescription.solution}`,
    `Result: ${project.fullDescription.result}`,
  ].join('\n\n');

  return (
    <div 
      ref={ref}
      className={`grid lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:grid-flow-dense' : ''}`}
    >
      {/* Image */}
      <div 
        className={`${!isEven ? 'lg:col-start-2' : ''} transition-all duration-1000 ${
          isInView 
            ? 'opacity-100 translate-x-0' 
            : isEven ? 'opacity-0 -translate-x-16' : 'opacity-0 translate-x-16'
        }`}
        style={{ transitionDelay: '200ms' }}
      >
        <Link 
          to={`/portfolio/${project.slug}`}
          className="group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer block"
        >
          <div className="relative w-full h-[500px]">
            {/* Green filter overlay */}
            <div className="absolute inset-0 bg-[#1B4332]/30 z-10 group-hover:opacity-0 transition-opacity duration-500"></div>
            <LazyImage 
              src={project.image} 
              alt={project.imageAlt}
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
              wrapperClassName="w-full h-full"
            />
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#1B4332]/90 via-[#1B4332]/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 z-20 flex items-end p-8">
              <div className="transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500">
                <span className="text-[#A7C4BC] text-sm font-medium tracking-wider uppercase">{project.category}</span>
                <h3 className="text-white text-2xl font-serif mt-2">{project.name}</h3>
              </div>
            </div>
            {/* Animated border on hover */}
            <div className="absolute inset-0 border-4 border-[#52B788] rounded-3xl opacity-0 group-hover:opacity-100 scale-95 group-hover:scale-100 transition-all duration-500 z-30 pointer-events-none"></div>
          </div>
        </Link>
      </div>

      {/* Content */}
      <div 
        className={`space-y-6 ${!isEven ? 'lg:col-start-1 lg:row-start-1' : ''} transition-all duration-1000 ${
          isInView 
            ? 'opacity-100 translate-x-0' 
            : isEven ? 'opacity-0 translate-x-16' : 'opacity-0 -translate-x-16'
        }`}
        style={{ transitionDelay: '400ms' }}
      >
        <div className="flex items-center gap-4">
          <span 
            className={`inline-block px-4 py-1.5 bg-[#52B788]/10 text-[#52796F] text-sm font-medium tracking-wider uppercase rounded-full transition-all duration-700 ${
              isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '500ms' }}
          >
            {project.category}
          </span>
          <span className="text-sm text-gray-400">•</span>
          <span className="text-sm text-gray-400">{project.year}</span>
        </div>

        <h2 
          className={`font-serif text-4xl lg:text-5xl text-[#2D3436] transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          {project.name}
        </h2>

        <div 
          className={`text-lg text-gray-600 leading-relaxed transition-all duration-700 max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-[#52B788]/50 scrollbar-track-gray-100 pr-2 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <p className="whitespace-pre-line">{descriptionText}</p>
        </div>

        {/* Tags */}
        <div 
          className={`flex flex-wrap gap-2 pt-2 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '800ms' }}
        >
          {project.tags.map((tag, tagIndex) => (
            <span 
              key={tag}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full hover:bg-[#52B788]/20 hover:text-[#1B4332] transition-all duration-300 cursor-default"
              style={{ 
                transitionDelay: `${800 + tagIndex * 50}ms`,
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div 
          className={`pt-4 flex items-center gap-6 transition-all duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '900ms' }}
        >
          <Link
            to={`/portfolio/${project.slug}`}
            className="inline-flex items-center gap-3 text-[#1B4332] font-semibold text-lg group/link relative overflow-hidden"
          >
            <span className="relative z-10">View Details</span>
            <div className="relative w-10 h-10 bg-[#1B4332] rounded-full flex items-center justify-center overflow-hidden group-hover/link:bg-[#52B788] transition-colors duration-300">
              <i className="ri-arrow-right-line text-white text-xl transition-transform duration-300 group-hover/link:translate-x-6"></i>
              <i className="ri-arrow-right-line text-white text-xl absolute -translate-x-6 transition-transform duration-300 group-hover/link:translate-x-0"></i>
            </div>
          </Link>
          <a
            href={project.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#1B4332] font-medium transition-all duration-300 group/ext"
          >
            <span>Live Site</span>
            <i className="ri-arrow-right-up-line text-xl group-hover/ext:rotate-45 transition-transform duration-300"></i>
          </a>
        </div>
      </div>
    </div>
  );
};

export default function PortfolioPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setIsHeaderVisible(true);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    setMousePosition({
      x: (e.clientX / window.innerWidth - 0.5) * 20,
      y: (e.clientY / window.innerHeight - 0.5) * 20,
    });
  };

  const filteredProjects = activeCategory === 'All' 
    ? allProjects 
    : allProjects.filter(p => p.category === activeCategory);

  // Generate particles
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    delay: Math.random() * 5,
    duration: 10 + Math.random() * 15,
    size: 4 + Math.random() * 8,
    left: `${Math.random() * 100}%`,
    top: `${Math.random() * 100}%`,
  }));

  return (
    <div 
      className="min-h-screen bg-white pt-32 pb-20 relative overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient blobs */}
        <div 
          className="absolute w-[600px] h-[600px] rounded-full bg-[#52B788]/5 blur-[100px] -top-48 -right-48"
          style={{ transform: `translate(${mousePosition.x * -0.5}px, ${mousePosition.y * -0.5}px)` }}
        />
        <div 
          className="absolute w-[500px] h-[500px] rounded-full bg-[#1B4332]/5 blur-[80px] top-1/2 -left-48"
          style={{ transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)` }}
        />
        <div 
          className="absolute w-[400px] h-[400px] rounded-full bg-[#A7C4BC]/10 blur-[60px] bottom-48 right-1/4"
          style={{ transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px)` }}
        />
        
        {/* Floating particles */}
        {particles.map((p) => (
          <Particle key={p.id} {...p} />
        ))}

        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(#1B4332 1px, transparent 1px), linear-gradient(90deg, #1B4332 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="max-w-[1060px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="mb-16 space-y-6">
          <div 
            className={`inline-block transition-all duration-700 ${
              isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="text-[#52796F] text-xs font-medium tracking-[0.15em] uppercase bg-[#52B788]/10 px-4 py-2 rounded-full">/Portfolio</span>
          </div>
          <h1 
            className={`font-serif text-6xl lg:text-7xl text-[#2D3436] transition-all duration-700 ${
              isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDelay: '200ms' }}
          >
            Selected{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-[#1B4332] via-[#52B788] to-[#1B4332] bg-[length:200%_auto] animate-gradient-text">
                Projects
              </span>
              <span className={`absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-[#52B788] to-[#1B4332] rounded-full transition-all duration-1000 ease-out ${isHeaderVisible ? 'w-full' : 'w-0'}`} style={{ transitionDelay: '800ms' }}></span>
            </span>
          </h1>
          <p 
            className={`text-xl text-gray-600 max-w-3xl leading-relaxed transition-all duration-700 ${
              isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ transitionDelay: '400ms' }}
          >
            Here are just a few examples from my portfolio. Each project represents a unique challenge solved with practical, results-driven development. Many more solutions have been delivered to satisfied clients.
          </p>
        </div>

        {/* Filter */}
        <div 
          className={`flex flex-wrap gap-3 mb-16 transition-all duration-700 ${
            isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '600ms' }}
        >
          {categories.map((category, index) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap relative overflow-hidden group ${
                activeCategory === category
                  ? 'bg-[#1B4332] text-white shadow-lg shadow-[#1B4332]/30'
                  : 'bg-white text-gray-600 border border-gray-300 hover:border-[#52796F] hover:text-[#1B4332]'
              }`}
              style={{ transitionDelay: `${600 + index * 100}ms` }}
            >
              <span className="relative z-10">{category}</span>
              {activeCategory !== category && (
                <span className="absolute inset-0 bg-[#52B788]/10 scale-0 group-hover:scale-100 transition-transform duration-300 rounded-full"></span>
              )}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="space-y-32">
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-32 text-center space-y-8 relative">
          {/* Decorative elements */}
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 border border-[#52B788]/20 rounded-full animate-spin-slow"></div>
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-16 h-16 border border-[#1B4332]/20 rounded-full animate-spin-reverse"></div>
          
          <h3 className="font-serif text-4xl text-[#2D3436] relative z-10">
            Have a project in mind?
          </h3>
          <Link 
            to="/contact"
            className="relative inline-flex items-center gap-4 bg-[#1B4332] text-white px-10 py-5 rounded-full transition-all duration-500 hover:shadow-[0_0_40px_rgba(27,67,50,0.4)] group whitespace-nowrap overflow-hidden"
          >
            {/* Animated gradient border */}
            <span className="absolute inset-0 rounded-full p-[2px] bg-gradient-to-r from-[#52B788] via-[#1B4332] to-[#52B788] bg-[length:200%_100%] animate-gradient-border opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="block w-full h-full bg-[#1B4332] rounded-full"></span>
            </span>
            <span className="relative font-semibold text-lg">Submit Project Inquiry</span>
            <div className="relative w-10 h-10 bg-white rounded-full flex items-center justify-center overflow-hidden">
              <i className="ri-arrow-right-line text-[#1B4332] text-xl transition-transform duration-300 group-hover:translate-x-6"></i>
              <i className="ri-arrow-right-line text-[#1B4332] text-xl absolute -translate-x-6 transition-transform duration-300 group-hover:translate-x-0"></i>
            </div>
          </Link>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-30px) rotate(180deg); opacity: 0.6; }
        }
        @keyframes gradient-text {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-border {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes spin-slow {
          from { transform: translateX(-50%) rotate(0deg); }
          to { transform: translateX(-50%) rotate(360deg); }
        }
        @keyframes spin-reverse {
          from { transform: translateX(-50%) rotate(360deg); }
          to { transform: translateX(-50%) rotate(0deg); }
        }
        .animate-float { animation: float linear infinite; }
        .animate-gradient-text { 
          background-size: 200% auto;
          animation: gradient-text 4s ease infinite; 
        }
        .animate-gradient-border { animation: gradient-border 3s linear infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-spin-reverse { animation: spin-reverse 15s linear infinite; }
      `}</style>
    </div>
  );
}
